import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  Grid,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CreditCase } from "../pages/DistripakoCreditosPage";

interface ContractViewerModalProps {
  creditCase: CreditCase | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function ContractViewerModal({
  creditCase,
  isOpen,
  onClose,
}: ContractViewerModalProps) {
  if (!isOpen || !creditCase) return null;

  const contract = creditCase.contract || {};
  const repLegalName = contract.repLegalName || creditCase.clientName || "";
  const repLegalDoc = contract.repLegalDoc || creditCase.documentNumber || "";
  const companyName = contract.companyName || creditCase.companyName || "N/A";
  const companyNit = contract.companyNit || creditCase.companyNit || "N/A";
  const city = contract.city || creditCase.city || "Medellín";
  const address = contract.address || creditCase.address || "Medellín, Colombia";
  const phone = contract.phone || creditCase.phone || "";
  const email = contract.email || creditCase.email || "";

  // Financial fees
  const successFeePct = contract.successFeePct || 8;
  const successFeeValue =
    contract.successFeeValue ||
    Math.round((creditCase.amount || 0) * (successFeePct / 100));
  const preApprovedFeePct = contract.preApprovedFeePct || 4;
  const preApprovedFeeValue =
    contract.preApprovedFeeValue ||
    Math.round((creditCase.amount || 0) * (preApprovedFeePct / 100));
  const virtualViabilityPct = contract.virtualViabilityPct || 2;
  const virtualViabilityValue =
    contract.virtualViabilityValue ||
    Math.round((creditCase.amount || 0) * (virtualViabilityPct / 100));

  const isAlreadySigned = !!contract.signed;

  const today = new Date();
  const day = contract.signedDateDay || String(today.getDate()).padStart(2, "0");
  const monthNames = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
  ];
  const month = contract.signedDateMonth || monthNames[today.getMonth()];
  const year = contract.signedDateYear || "2026";

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          bgcolor: "#ffffff",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)",
          border: "1px solid #e2e8f0",
          maxHeight: "92vh",
        },
      }}
    >
      <style>{`
        @media print {
          @page {
            margin: 12mm 15mm;
            size: auto;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
          }
          body * {
            visibility: hidden !important;
          }
          #admin-contract-print-area, #admin-contract-print-area * {
            visibility: visible !important;
          }
          #admin-contract-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
            color: #000000 !important;
            box-shadow: none !important;
          }
        }
      `}</style>

      {/* TOP BAR EXACTLY LIKE NEXT-FRONT */}
      <Box
        className="print:hidden"
        sx={{
          bgcolor: "#0f172a",
          color: "#ffffff",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #1e293b",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              bgcolor: "rgba(37, 99, 235, 0.2)",
              border: "1px solid rgba(59, 130, 246, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#60a5fa",
            }}
          >
            <DescriptionIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={800} color="#ffffff" lineHeight={1.2}>
              Contrato de Prestación de Servicios - Gestión Comercial Financiera
            </Typography>
            <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.72rem" }}>
              NEXT CONNECTION GROUP SAS • NIT 901500993-2 • Radicado: {creditCase.code}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            onClick={handlePrint}
            startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
            sx={{
              bgcolor: "#1e293b",
              color: "#e2e8f0",
              border: "1px solid #334155",
              borderRadius: "10px",
              px: 2,
              py: 0.7,
              fontSize: "0.75rem",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { bgcolor: "#334155", color: "#ffffff" },
            }}
          >
            Imprimir / PDF
          </Button>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{ color: "#94a3b8", "&:hover": { color: "#ffffff", bgcolor: "#1e293b" } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* DOCUMENT BODY EXACTLY LIKE NEXT-FRONT */}
      <DialogContent
        id="admin-contract-print-area"
        sx={{
          p: { xs: 3, sm: 6 },
          bgcolor: "#ffffff",
          fontFamily: "Georgia, Cambria, 'Times New Roman', Times, serif",
          color: "#1e293b",
          lineHeight: 1.8,
          fontSize: "0.88rem",
          overflowY: "auto",
          maxHeight: "calc(92vh - 70px)",
        }}
      >
        {/* Document Header with Official NEXT Logo */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pb: 3, borderBottom: "1px solid #e2e8f0", mb: 3 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="NEXT Connection Group Logo"
            sx={{
              height: { xs: 80, sm: 110 },
              maxWidth: 280,
              width: "auto",
              objectFit: "contain",
              mb: 1.5,
            }}
          />
          <Typography
            sx={{
              fontSize: "11px",
              letterSpacing: 2,
              textTransform: "uppercase",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              fontWeight: 800,
              color: "#475569",
            }}
          >
            NEXT CONNECTION GROUP SAS • NIT 901500993-2
          </Typography>

          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              fontWeight: 900,
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
              color: "#0f172a",
              mt: 2,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              lineHeight: 1.3,
            }}
          >
            CONTRATO DE PRESTACIÓN DE SERVICIOS
            <br />
            <span style={{ color: "#1e3a8a" }}>GESTIÓN COMERCIAL FINANCIERA</span>
          </Typography>
        </Box>

        {/* Parties Introduction */}
        <Typography paragraph sx={{ textAlign: "justify", mb: 2 }}>
          Entre, <strong>DIANA PATRICIA DUQUE CAMPILLO</strong>, mayor de edad, e identificada con cédula de ciudadanía <strong>Nº 43.593.567</strong>, en calidad de representante legal de <strong>NEXT CONNECTION GROUP SAS</strong> NIT <strong>901500993-2</strong>, con domicilio en la ciudad de <strong>Medellín</strong>, y{" "}
          <span style={{ fontWeight: 800, textDecoration: "underline", color: "#172554" }}>
            {repLegalName || "________________________"}
          </span>
          , mayor de edad, e identificado (a) con cédula de ciudadanía <strong>Nº {repLegalDoc || "____________"}</strong>, actuando en nombre propio y en calidad de representante legal de{" "}
          <span style={{ fontWeight: 800, textDecoration: "underline", color: "#172554" }}>
            {companyName !== "N/A" ? companyName : "________________________"}
          </span>{" "}
          NIT{" "}
          <span style={{ fontWeight: 800, textDecoration: "underline", color: "#172554" }}>
            {companyNit !== "N/A" ? companyNit : "____________"}
          </span>
          , con domicilio principal en la ciudad de{" "}
          <span style={{ fontWeight: 800, textDecoration: "underline", color: "#172554" }}>
            {city || "Medellín"}
          </span>
          , quien en adelante se denomina <strong>EL CLIENTE</strong>, acordamos celebrar el presente contrato de prestación de servicios para la <strong>GESTIÓN COMERCIAL FINANCIERA</strong>, con fundamento en las siguientes:
        </Typography>

        <Typography
          sx={{
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontWeight: 800,
            textAlign: "center",
            fontSize: "0.85rem",
            textTransform: "uppercase",
            letterSpacing: 3,
            color: "#0f172a",
            py: 1,
            my: 1,
          }}
        >
          CLÁUSULAS
        </Typography>

        {/* Cláusula Primera */}
        <Typography paragraph sx={{ textAlign: "justify", mb: 2 }}>
          <strong>PRIMERA: OBJETO DE CONTRATO:</strong> En virtud del presente contrato <strong>NEXT CONNECTION GROUP SAS</strong>, se compromete con el <strong>CLIENTE</strong>, a realizar las gestiones ante las entidades financieras, vigiladas por la Superintendencia Financiera o quien haga sus veces, para la aprobación y desembolsos de productos financieros tales como crédito de libre inversión, cupos de crédito rotativo, Tarjetas de Crédito, cupos de Factoring, cupos de Confirming, entre otros.
        </Typography>

        {/* Cláusula Segunda */}
        <Box sx={{ mb: 2 }}>
          <Typography paragraph sx={{ textAlign: "justify", mb: 1 }}>
            <strong>SEGUNDA: HONORARIOS:</strong> El <strong>CLIENTE</strong>, se obliga y compromete a cancelar a la <strong>INTERMEDIARIO FINANCIERO</strong>, los siguientes valores:
          </Typography>
          <Box component="ol" sx={{ pl: 3, m: 0, "& li": { mb: 1, textAlign: "justify" } }}>
            <li>
              La Comisión por estudio de crédito, incluye: consulta y perfilamiento de cliente en Datacrédito, verificación de documentos, y análisis financiero, para estudio de crédito con las diferentes entidades financieras, bancarias o cooperativas, y aseguradoras, tendrá un costo de <strong>$ 250.000 mil pesos</strong>, una vez el crédito sea APROBADO, DESEMBOLSADO Y SEA PAGADA LA COMISION, por parte del cliente, estos son reembolsables por parte de <strong>NEXT CONNECTION GROUP SAS</strong>. <strong>Cuenta Corriente # 29899994884 BANCOLOMBIA, NIT 901500993</strong>.
            </li>
            <li>
              Comisión de éxito del <strong>{successFeePct}%</strong> sobre el valor del crédito, incluye crédito de libre inversión, cupos de crédito rotativo, Tarjetas de Crédito, cupos de Factoring, cupos de Confirming, entre otros que sean aprobados y/o desembolsados, que equivale al valor estimado de <strong>${successFeeValue.toLocaleString("es-CO")} COP</strong>.
            </li>
            <li>
              Comisión del <strong>{preApprovedFeePct}%</strong> sobre el crédito PRE APROBADO O APROBADO, pero que por cualquier razón ajena a la voluntad de <strong>NEXT CONNECTION GROUP SAS</strong>, el <strong>CLIENTE</strong> no lo quiso TOMAR, y después de haber hecho el proceso con <strong>NEXT CONNECTION GROUP SAS</strong>, lo tramite en alguna oficina diferente de la entidad que aprobó la operación de crédito o lo tomo después de tres meses, esta comisión se genera, que equivale al valor de <strong>${preApprovedFeeValue.toLocaleString("es-CO")} COP</strong>.
            </li>
            <li>
              Comisión del <strong>{virtualViabilityPct}%</strong> por viabilidades virtuales, que equivale al valor de <strong>${virtualViabilityValue.toLocaleString("es-CO")} COP</strong>.
            </li>
          </Box>
        </Box>

        {/* Cláusula Tercera */}
        <Typography paragraph sx={{ textAlign: "justify", mb: 2 }}>
          <strong>TERCERA. – FORMA DE PAGO:</strong> EL <strong>CLIENTE</strong>, deberá cancelar dentro de los 3 días calendario siguientes el valor de la comisión de acuerdo con lo estipulado en la cláusula segunda de este contrato, a la <strong>cuenta corriente N° 29899994884 Bancolombia</strong>.
        </Typography>

        {/* Cláusula Cuarta */}
        <Typography paragraph sx={{ textAlign: "justify", mb: 2 }}>
          <strong>CUARTA. - NATURALEZA DEL CONTRATO:</strong> Se trata de un contrato de intermediación, en donde <strong>NEXT CONNECTION GROUP SAS</strong>, bajo su experticia y conocimiento, pone en contacto al <strong>CLIENTE</strong>, con la entidad financiera, para la gestión y trámite del producto financiero solicitado y según su perfil se tramitará por las diferentes entidades con las que se tiene relación comercial a favor del <strong>CLIENTE</strong>. Por lo tanto, el no cumplimiento de las obligaciones contractuales, o las discrepancias entre la entidad financiera y el <strong>CLIENTE</strong>, será del resorte exclusivo de estas dos partes, por lo que <strong>NEXT CONNECTION GROUP SAS</strong>, se mantendrá indemne ante estas situaciones.
        </Typography>

        {/* Cláusula Quinta */}
        <Typography paragraph sx={{ textAlign: "justify", mb: 2 }}>
          <strong>QUINTA. - OBLIGACIONES:</strong> Las partes establecen que el presente contrato es de medio y no de resultados, por lo que <strong>NEXT CONNECTION GROUP SAS</strong>, deberá poner al servicio del <strong>CLIENTE</strong>, toda la idoneidad, experticia y responsabilidad, para lograr obtener el resultado. Y este a su vez deberá dar a conocer al <strong>NEXT CONNECTION GROUP SAS</strong>, toda la información necesaria para la aprobación del producto financiero y pagar las comisiones de acuerdo con lo estipulado en la cláusula segunda.
        </Typography>

        {/* Cláusula Sexta */}
        <Typography paragraph sx={{ textAlign: "justify", mb: 2 }}>
          <strong>SEXTA. - REPRESENTANTES DE NEXT CONNECTION GROUP SAS:</strong> El <strong>CLIENTE</strong>, conoce y acepta que <strong>NEXT CONNECTION GROUP SAS</strong>, podrá tener representantes que actúen a su nombre, sin embargo, el pago de los honorarios establecidos en la cláusula segunda de este contrato deberá ser cancelado directamente a <strong>NEXT CONNECTION GROUP SAS</strong>, y no a la cuenta de ningún representante de NEXT CONNECTION GROUP SAS. En caso de que el <strong>CLIENTE</strong>, consigne, deposite o pague a favor de un tercero, las comisiones estipuladas en este contrato, sin contar con la autorización expresa de <strong>NEXT CONNECTION GROUP SAS</strong>, dicho pago se tendrá como no realizado y el <strong>CLIENTE</strong> deberá volver a realizar el pago.
        </Typography>

        {/* Cláusula Séptima */}
        <Typography paragraph sx={{ textAlign: "justify", mb: 2 }}>
          <strong>SÉPTIMA. - PLAZO DE EJECUCIÓN:</strong> Este contrato se realiza por tiempo indefinido. No requiere preaviso para su terminación, sin embargo, en el evento que el <strong>CLIENTE</strong>, decida dar por terminado el contrato, dentro de las gestiones que realice el <strong>NEXT CONNECTION GROUP SAS</strong>, este deberá cancelar las comisiones estipuladas.
        </Typography>

        {/* Cláusula Octava */}
        <Box sx={{ mb: 2 }}>
          <Typography paragraph sx={{ textAlign: "justify", mb: 0.5 }}>
            <strong>OCTAVA: REPORTES:</strong> El cliente autoriza, acepta y conoce que, en caso de mora en el cumplimiento de su obligación:
          </Typography>
          <Box component="ol" sx={{ pl: 3, m: 0, "& li": { mb: 0.5, textAlign: "justify" } }}>
            <li>Será reportado a las centrales de riesgo que tenga contratada la empresa <strong>NEXT CONNECTION GROUP SAS</strong>.</li>
            <li>Se iniciarán los procesos judiciales respectivos y se procederá a solicitar medidas cautelares correspondientes, tales como embargos y secuestros de bienes y cuentas bancarias.</li>
          </Box>
        </Box>

        {/* Cláusula Novena */}
        <Typography paragraph sx={{ textAlign: "justify", mb: 2 }}>
          <strong>NOVENA:</strong> El cliente autoriza que la empresa <strong>NEXT CONNECTION GROUP SAS</strong>, pueda llenar los espacios en blanco, en lo que hace referencia al valor del crédito aprobado y/o desembolsado, el cual deberá estar acompañado del certificado de aprobación del crédito.
        </Typography>

        {/* Cláusula Décima */}
        <Typography paragraph sx={{ textAlign: "justify", mb: 2 }}>
          <strong>DÉCIMA:</strong> El presente contrato presta mérito ejecutivo, y las partes renuncian desde ya a cualquier requerimiento judicial o extrajudicial para la constitución en mora.
        </Typography>

        {/* Agreement Confirmation */}
        <Typography paragraph sx={{ textAlign: "justify", mb: 2 }}>
          Las partes manifiestan haber leído la totalidad del contenido del presente contrato, a fin de verificar la exactitud de todos los datos en él consignados, y/o aclarar, modificar, corregir, adicionar o suprimir sus cláusulas; por lo tanto, cualquier imprecisión o inexactitud en el mismo, es completa responsabilidad de las partes.
        </Typography>

        {/* Subscription Date */}
        <Typography paragraph sx={{ textAlign: "justify", fontWeight: 700, mb: 3 }}>
          De conformidad con lo anterior las partes suscriben el presente documento en dos ejemplares en el mismo tenor y valor, a los <u>&nbsp;&nbsp;{day}&nbsp;&nbsp;</u> días del mes de <u>&nbsp;&nbsp;{month}&nbsp;&nbsp;</u> de <strong>{year}</strong>.
        </Typography>

        {/* SIGNATURES SECTION EXACTLY LIKE NEXT-FRONT */}
        <Box
          sx={{
            pt: 3,
            borderTop: "1px solid #e2e8f0",
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          <Grid container spacing={4}>
            {/* NEXT Connection Group Legal Signature */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ height: 75, display: "flex", alignItems: "flex-end" }}>
                  <Typography
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontStyle: "italic",
                      color: "#1e3a8a",
                      fontWeight: 800,
                      fontSize: "1.15rem",
                      borderBottom: "2px solid #1e293b",
                      pb: 0.5,
                      width: "100%",
                      maxWidth: 260,
                    }}
                  >
                    Diana Patricia Duque C.
                  </Typography>
                </Box>
                <Box sx={{ fontSize: "0.78rem", mt: 0.5 }}>
                  <Typography variant="body2" fontWeight={800} color="#0f172a">
                    DIANA PATRICIA DUQUE CAMPILLO
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Representante Legal
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#1e3a8a", fontWeight: 800, display: "block" }}>
                    NEXT CONNECTION GROUP SAS
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", fontFamily: "monospace", fontSize: "11px", display: "block" }}>
                    NIT 901500993-2
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Client Signature */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ height: 75, display: "flex", alignItems: "flex-end" }}>
                  {isAlreadySigned || contract.signatureDataUrl ? (
                    <Box
                      sx={{
                        borderBottom: "2px solid #1e293b",
                        pb: 0.5,
                        width: "100%",
                        maxWidth: 260,
                      }}
                    >
                      <Box
                        component="img"
                        src={contract.signatureDataUrl}
                        alt="Firma del Cliente"
                        sx={{ height: 60, objectFit: "contain", display: "block" }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        borderBottom: "2px dashed #94a3b8",
                        pb: 0.5,
                        width: "100%",
                        maxWidth: 260,
                        color: "#94a3b8",
                        fontStyle: "italic",
                        fontSize: "0.75rem",
                      }}
                    >
                      [Firma digital pendiente de formalizar]
                    </Box>
                  )}
                </Box>

                <Box sx={{ fontSize: "0.78rem", mt: 0.5, lineHeight: 1.5 }}>
                  <Typography variant="body2" fontWeight={800} color="#0f172a">
                    EL CLIENTE: {repLegalName || "_________________"}
                  </Typography>
                  <Typography variant="caption" display="block" color="#475569">
                    <strong style={{ color: "#64748b" }}>CÉDULA:</strong>{" "}
                    <span style={{ fontFamily: "monospace", fontWeight: 700 }}>{repLegalDoc || "_________________"}</span>
                  </Typography>
                  <Typography variant="caption" display="block" color="#475569">
                    <strong style={{ color: "#64748b" }}>EMPRESA:</strong> {companyName}
                  </Typography>
                  <Typography variant="caption" display="block" color="#475569">
                    <strong style={{ color: "#64748b" }}>NIT:</strong>{" "}
                    <span style={{ fontFamily: "monospace" }}>{companyNit}</span>
                  </Typography>
                  <Typography variant="caption" display="block" color="#475569">
                    <strong style={{ color: "#64748b" }}>CEL:</strong> {phone || "_________________"}
                  </Typography>
                  <Typography variant="caption" display="block" color="#475569">
                    <strong style={{ color: "#64748b" }}>DIRECCIÓN:</strong> {address}
                  </Typography>
                  <Typography variant="caption" display="block" color="#475569">
                    <strong style={{ color: "#64748b" }}>MAIL:</strong> {email || "_________________"}
                  </Typography>

                  {isAlreadySigned && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                        label={`Suscrito electrónicamente el ${day} de ${month} de ${year}`}
                        size="small"
                        sx={{
                          bgcolor: "#ecfdf5",
                          color: "#047857",
                          fontWeight: 800,
                          fontSize: "0.7rem",
                          border: "1px solid #a7f3d0",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
